import React, { useEffect, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { editOpportunityPath, editQuotePath, newOpportunityPath } from "../../router/paths";
import { getFeatureFlagsConfiguration, getGlobalConfiguration } from "../../utils/getGlobalConfiguration";
import { featureFlags } from "../../components/FeatureFlag/featureFlagConst";
import { showMultipleToasts } from "../../utils/apiSetup";
import { isCustomEvent } from "../guards";
import { useSelector } from "react-redux";
import { State } from "../../configureStore";
import { useAtom } from "jotai";
import { featureFlagsChangeAtom } from "../../atoms/atoms";

interface OpportunitiesProps {
  id: string;
}

const Opportunities = memo(function Opportunities({ id }: OpportunitiesProps) {
  const [eventListenerRef, setEventListenerRef] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const history = useHistory();
  const isLoading = useSelector((state: State) => state.api.configurations.agentConfigurations.isFetching);
  const [featureFlagValues] = useAtom(featureFlagsChangeAtom);

  const opportunityFeatures = getFeatureFlagsConfiguration("customer.customerView.opportunityActions");

  const featureFlag = JSON.stringify({
    opportunity: {
      bundleDiscount: getFeatureFlagsConfiguration(featureFlags.customer.editOpportunity.basket.bundleDiscount),
      newOpportunityButton: getFeatureFlagsConfiguration("customer.customerView.newOpportunityButton"),
      ...opportunityFeatures,
    },
    quote: getFeatureFlagsConfiguration("customer.customerView.opportunityPolicyActions"),
  });

  const translations = JSON.stringify({
    quote: t("portal:widgets.quoteList", {
      returnObjects: true,
      interpolation: { skipOnVariables: true },
    }),
    opportunity: t("portal:widgets.opportunitiesList", {
      returnObjects: true,
      interpolation: { skipOnVariables: true },
    }),
    policyStatus: t("portal:policyStatus", {
      returnObjects: true,
      interpolation: { skipOnVariables: true },
    }),
  });

  const styles = JSON.stringify({
    components: {
      WidgetCard: {
        contentContainerPadding: "14px",
      },
    },
  });

  const pageSize = getGlobalConfiguration("portfolioWidgets.opportunitiesPageSize");

  useEffect(() => {
    const handleReturnErrors = (event: Event) => {
      if (!isCustomEvent<{ errors?: string[] }>(event)) return;

      const { errors } = event.detail;

      if (errors) {
        showMultipleToasts(errors);
      }
    };

    const handleReturnMessages = (event: Event) => {
      if (!isCustomEvent<{ messages?: string[] }>(event)) return;

      const { messages } = event.detail;

      if (messages) {
        showMultipleToasts(messages);
      }
    };

    const handleNewOpportunityClick = () => {
      history.push(newOpportunityPath(id));
    };

    const editQuote = (event: Event) => {
      const { uuid } = (event as CustomEvent<{ uuid: string }>).detail;

      history.push(editQuotePath({ customerId: id, quoteId: uuid }));
    };

    const editOpportunity = (event: Event) => {
      const { opportunityId } = (event as CustomEvent<{ opportunityId: string }>).detail;

      history.push(editOpportunityPath(id, opportunityId));
    };

    const handleOnSuccessAction = (event: Event) => {
      if (!isCustomEvent<{ messages?: string[]; action?: string }>(event)) return;

      const { messages, action } = event.detail;

      if (action === "fetch-opportunities") {
        return;
      }

      if (messages) {
        showMultipleToasts(messages);
      }
    };

    if (eventListenerRef) {
      eventListenerRef.addEventListener("new-opportunity", handleNewOpportunityClick);
      eventListenerRef.addEventListener("quote-edit", editQuote);
      eventListenerRef.addEventListener("opportunity-edit", editOpportunity);
      eventListenerRef.addEventListener("success-widget-action", handleOnSuccessAction);
      eventListenerRef.addEventListener("widget-message", handleReturnMessages);
      eventListenerRef.addEventListener("widget-error", handleReturnErrors);
    }

    return () => {
      if (eventListenerRef) {
        eventListenerRef.removeEventListener("new-opportunity", handleNewOpportunityClick);
        eventListenerRef.removeEventListener("quote-edit", editQuote);
        eventListenerRef.removeEventListener("opportunity-edit", editOpportunity);
        eventListenerRef.removeEventListener("success-widget-action", handleOnSuccessAction);
        eventListenerRef.removeEventListener("widget-message", handleReturnMessages);
        eventListenerRef.removeEventListener("widget-error", handleReturnErrors);
      }
    };
  }, [eventListenerRef, id, history]);

  //  Widget must not render until feature flags are resolved.
  //  featureFlagValues is null on first render (before API responds),
  //  and isLoading covers the gap while agentConfigurations API is in flight.
  //  Without this guard, the widget renders with fallback flag values and flickers.
  if (isLoading || !featureFlagValues) {
    return null;
  }

  return (
    <ds-portfolio-opportunity-widget
      pagesize={pageSize}
      ref={setEventListenerRef}
      variant={"opportunity"}
      partyid={id}
      translations={translations}
      featureflags={featureFlag}
      styleoverrides={styles}
    />
  );
});

export default Opportunities;
